import * as React from 'react';
import {Dialog} from '@base-ui/react/dialog';
import {clsx} from 'clsx';

import {Visibility, VisibilityEvents} from 'common/components/base/stacking-context';
import {ButtonReact} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {confirm} from 'common/components/confirm';
import {THEME as ConfirmTheme} from 'common/components/confirm/types';
import {getFocusableChildren} from 'common/util/focusable';
import type {LocaleSafeString} from 'common/i18n';
import {useTranslation} from 'common/i18n/hooks';
import {withStyles} from 'common/util/react';
import {
  getStackingDepth,
  STACKABLE_CONTEXT_ATTR,
  StackableEvent,
  useStackable,
} from 'common/components/base/use-stackable';

import {ModalChangeEvent, ModalHandle, ModalType, VerticalAlignment} from './types';
import styles from './index.module.styl';
import css from './index.module.styl?inline';

interface ModalOwnProps {
  open: boolean;
  closeable?: boolean;
  growToContent?: boolean;
  hideBackdrop?: boolean;
  hideBackground?: boolean;
  hideCloseButton?: boolean;
  modalType?: ModalType;
  modalTitle?: string;
  modalDescription?: string;
  notFullscreen?: boolean;
  verticalAlignment?: VerticalAlignment;
  closeConfirmText?: LocaleSafeString;
  slidingTransition?: boolean;
  closeButtonPadded?: boolean;
  /** Opt out of stacking bookkeeping and `stackable-*` events. */
  ignoreStacking?: boolean;
  /** Close when a peer of the same `modalType` opens, instead of letting the two stack. */
  closeOfSameType?: boolean;
  /** Ref name forwarded onto the `<mp-modal>` host as an attribute. */
  elref?: string;
  /** Host CSS injected into the shadow tree, for Panel callers that style the modal's internals. */
  styleOverride?: string;
  /** Fired on open and close with `detail.state` of `Visibility.Open | Closed`. */
  onChange?: (ev: ModalChangeEvent) => void;
  onStackableOpen?: (ev: StackableEvent) => void;
  onStackableClosed?: (ev: StackableEvent) => void;
  children?: React.ReactNode;
}

export type ModalProps = ModalOwnProps & Omit<React.HTMLAttributes<HTMLElement>, keyof ModalOwnProps | `onChange`>;

const BACKDROP_TYPES = [
  ModalType.Modal,
  ModalType.RightDrawer,
  ModalType.RightDrawerUnderHeader,
  ModalType.BottomDrawer,
];

// `class`, not `className`: React only maps `className` on known HTML elements, and would emit a
// literal `classname="..."` here.
const ModalHost = `mp-modal` as unknown as React.FC<
  Omit<React.HTMLAttributes<HTMLElement>, `className`> & {
    ref?: React.Ref<HTMLElement>;
    elref?: string;
    class?: string;
    children?: React.ReactNode;
  }
>;

const TYPE_MODAL_CLASS: Record<ModalType, string | undefined> = {
  [ModalType.Popup]: styles.isPopup,
  [ModalType.Modal]: styles.isModal,
  [ModalType.RightDrawer]: styles.isRightDrawer,
  [ModalType.RightDrawerUnderHeader]: styles.isRightDrawerUnderHeader,
  [ModalType.BottomDrawer]: styles.isBottomDrawer,
  [ModalType.TourModal]: styles.isTourModal,
};

// Center and bottom rely on the main box's `margin: auto`; only top needs a class.
const ALIGNMENT_CLASS: Record<VerticalAlignment, string | undefined> = {
  [VerticalAlignment.Top]: styles.isTopAligned,
  [VerticalAlignment.Center]: undefined,
  [VerticalAlignment.Bottom]: undefined,
};

function Modal(
  {
    open,
    closeable = false,
    growToContent = false,
    hideBackdrop = false,
    hideBackground = false,
    hideCloseButton = false,
    modalType = ModalType.Modal,
    modalTitle,
    modalDescription: _modalDescription,
    notFullscreen = false,
    verticalAlignment = VerticalAlignment.Center,
    closeConfirmText,
    slidingTransition = false,
    closeButtonPadded = false,
    styleOverride,
    ignoreStacking = false,
    closeOfSameType = false,
    elref,
    onChange,
    onStackableOpen,
    onStackableClosed,
    className,
    style,
    children,
    ...hostRest
  }: ModalProps,
  ref: React.Ref<ModalHandle>,
) {
  const {t} = useTranslation();
  const hostRef = React.useRef<HTMLElement>(null);
  const lastVisibilityRef = React.useRef<Visibility>(Visibility.Closed);
  const [isShowing, setIsShowing] = React.useState(open);
  const [container, setContainer] = React.useState<HTMLElement | ShadowRoot | null>(null);

  const {stackId, notifyOpen, notifyClosed, release} = useStackable({ignoreStacking});

  // Read through a ref so the transition callbacks keep a stable identity across renders.
  const callbacksRef = React.useRef({onChange, onStackableOpen, onStackableClosed});
  callbacksRef.current = {onChange, onStackableOpen, onStackableClosed};

  const showBackdrop = BACKDROP_TYPES.includes(modalType) && !hideBackdrop;
  const showCloseButton = (closeable || modalType === ModalType.TourModal) && !hideCloseButton;

  const notifyStack = React.useCallback(
    (next: Visibility, host: HTMLElement | null) => {
      const isOpen = next === Visibility.Open;
      const stackEvent = isOpen ? notifyOpen(host) : notifyClosed(host);
      if (stackEvent) {
        const {onStackableOpen: openHandler, onStackableClosed: closedHandler} = callbacksRef.current;
        (isOpen ? openHandler : closedHandler)?.(stackEvent);
      }
    },
    [notifyOpen, notifyClosed],
  );

  const transitionTo = React.useCallback(
    (next: Visibility) => {
      if (lastVisibilityRef.current === next) {
        return;
      }
      lastVisibilityRef.current = next;
      setIsShowing(next === Visibility.Open);
      const host = hostRef.current;

      // A real CustomEvent from the host so consumer guards reading `ev.target.localName` work.
      const changeEvent = new CustomEvent(`change`, {detail: {state: next}}) as ModalChangeEvent;
      host?.dispatchEvent(changeEvent);
      callbacksRef.current.onChange?.(changeEvent);
      notifyStack(next, host);
    },
    [notifyStack],
  );

  const requestClose = React.useCallback(async () => {
    if (closeConfirmText) {
      const shouldClose = await confirm(
        {description: closeConfirmText, theme: ConfirmTheme.DELETE},
        {},
        {
          title: ({t: translate}) => translate(`components.modal.close-window`),
          confirmButtonLabel: ({t: translate}) => translate(`components.modal.close`),
        },
      );
      if (!shouldClose) {
        return;
      }
    }
    transitionTo(Visibility.Closed);
  }, [closeConfirmText, transitionTo]);

  React.useImperativeHandle(ref, () => ({close: () => void requestClose()}), [requestClose]);

  React.useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return;
    }
    // promisifyModal matches this host's stackable-closed events by stackId.
    (host as HTMLElement & {stackId?: string}).stackId = stackId;
    // Base UI requires a portal, so the container is whatever keeps the modal where the caller put
    // it: the shim's shadow root, whose children inherit the width consumer stylesheets set on the
    // react2panel wrapper, or the host itself, so the caller's descendant selectors still match.
    const root = host.getRootNode();
    setContainer(root instanceof ShadowRoot ? root : host);
  }, [stackId]);

  React.useEffect(() => {
    transitionTo(open ? Visibility.Open : Visibility.Closed);
  }, [open, transitionTo]);

  // The drawers that pass `closeOfSameType` are opened one per table row, so a second instance
  // replaces the first rather than stacking on it. Only while this one is the whole stack, matching
  // the depth gate Stackable applies -- a peer opening above a nested modal leaves it alone.
  React.useEffect(() => {
    if (!closeOfSameType || !isShowing) {
      return undefined;
    }
    const closeForSameTypePeer = (ev: Event) => {
      if ((ev as StackableEvent).detail?.target === stackId || getStackingDepth() !== 1) {
        return;
      }
      const opener = ev.composedPath()[0] as Element | undefined;
      if (opener?.getAttribute?.(`modal-type`) === modalType) {
        transitionTo(Visibility.Closed);
      }
    };
    window.addEventListener(VisibilityEvents[Visibility.Open], closeForSameTypePeer);
    return () => window.removeEventListener(VisibilityEvents[Visibility.Open], closeForSameTypePeer);
  }, [closeOfSameType, isShowing, stackId, modalType, transitionTo]);

  // On unmount while open, balance the stacking counter and notify consumers.
  React.useEffect(
    () => () => {
      if (lastVisibilityRef.current === Visibility.Open) {
        lastVisibilityRef.current = Visibility.Closed;
        notifyStack(Visibility.Closed, hostRef.current);
      } else {
        release();
      }
    },
    [notifyStack, release],
  );

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean, details: Dialog.Root.ChangeEventDetails) => {
      if (nextOpen) {
        return;
      }
      if ((details.reason === `escape-key` || details.reason === `outside-press`) && closeable) {
        void requestClose();
      }
    },
    [closeable, requestClose],
  );

  const handleWrapperMouseDown = React.useCallback(() => {
    if (closeable) {
      void requestClose();
    }
  }, [closeable, requestClose]);

  const typeClass = TYPE_MODAL_CLASS[modalType];

  // Labeling attributes describe the dialog, so they ride on the popup that carries role="dialog"
  // and the content; the rest stay on the always-mounted host that owns events and stacking.
  const entries = Object.entries(hostRest);
  const isLabeling = (key: string) => /^(role|id|title|aria-|data-)/.test(key);
  const identityProps: Record<string, unknown> = Object.fromEntries(entries.filter(([key]) => isLabeling(key)));
  const hostProps = Object.fromEntries(entries.filter(([key]) => !isLabeling(key)));
  // Base UI supplies the role but not a name.
  if (modalTitle && !identityProps[`aria-label`] && !identityProps[`aria-labelledby`]) {
    identityProps[`aria-label`] = modalTitle;
  }

  return (
    // `open` only when true: React would stringify `open={false}` to `open="false"`, which
    // `hasAttribute` still reports as present.
    <ModalHost
      ref={hostRef}
      elref={elref}
      class={clsx(styles.host, className)}
      style={style}
      // Peers read each other's type off the host, the way the Panel modal exposed it.
      {...{'modal-type': modalType}}
      {...(open ? {open: ``} : {})}
      {...hostProps}
    >
      {container && (
        <Dialog.Root open={isShowing} modal={showBackdrop ? true : `trap-focus`} onOpenChange={handleOpenChange}>
          <Dialog.Portal container={container}>
            {showBackdrop && (
              <Dialog.Backdrop className={clsx(styles.backdrop, {[styles.sliding]: slidingTransition})} />
            )}
            <Dialog.Popup
              className={clsx(styles.modal, typeClass, {
                [styles.isFullscreen]: !notFullscreen,
                [styles.sliding]: slidingTransition,
                // Portaled into the shadow root, so it can inherit `.mp-modal.foo { width }`.
                [styles.hostSized]: container instanceof ShadowRoot,
              })}
              {...{[STACKABLE_CONTEXT_ATTR]: ``}}
              // Match Stackable: focus the caller's content first. Base UI can't see content
              // projected through the <slot>, so resolve that case by hand.
              initialFocus={() => {
                const shimHost = container instanceof ShadowRoot ? (container.host as HTMLElement) : null;
                return (shimHost ? [...getFocusableChildren(shimHost)][0] : undefined) ?? true;
              }}
              render={(popupProps) => {
                // Same `hidden={false}` stringification hazard as `open` above.
                const {
                  hidden: popupHidden,
                  className: popupClassName,
                  ...restPopupProps
                } = popupProps as React.HTMLAttributes<HTMLElement>;
                return (
                  <ModalHost
                    {...restPopupProps}
                    // The popup is portaled out of the host, so the consumer class and style have
                    // to ride along to reach the box that carries the layout. The host is
                    // `display: contents`, so a width left behind on it sizes nothing.
                    class={clsx(popupClassName, className)}
                    style={{...(restPopupProps.style ?? {}), ...style}}
                    {...(popupHidden ? {hidden: true} : {})}
                    {...identityProps}
                  />
                );
              }}
            >
              <div
                className={clsx(`mp-modal-wrapper`, styles.wrapper, typeClass, ALIGNMENT_CLASS[verticalAlignment])}
                onMouseDown={handleWrapperMouseDown}
              >
                <div
                  className={clsx(`mp-modal-main`, styles.main, typeClass, {
                    [styles.isVariableWidth]: growToContent,
                    [styles.hasBackground]: !hideBackground,
                  })}
                  onMouseDown={(ev) => ev.stopPropagation()}
                >
                  {styleOverride ? <style>{styleOverride}</style> : null}
                  {children}
                  {/* Projects children forwarded by the `<ModalEl>` shim. */}
                  <slot />
                  {/* Absolutely positioned, so rendering last only affects focus and tab order. */}
                  {showCloseButton && (
                    <ButtonReact
                      className={clsx(`mp-modal-close-button`, styles.closeButton, {
                        [styles.padded]: closeButtonPadded,
                      })}
                      elref={`closeModalButton`}
                      theme={ButtonTheme.Tertiary}
                      icon='x'
                      label={t(`components.modal.close-modal`)}
                      onClick={() => void requestClose()}
                    />
                  )}
                </div>
              </div>
            </Dialog.Popup>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </ModalHost>
  );
}

export const ModalReact = withStyles(React.forwardRef(Modal), css);
